import {
  getCurrentMainAssetPath,
  getManifestBuildIdentifier,
  getManifestMainAssetPath,
  normalizeAssetPath,
} from "./buildIdentifier";

const BUILD_MANIFEST_PATH = "/asset-manifest.json";
const BUILD_REFRESH_POLICY_PATH = "/build-refresh-policy.json";
const BUILD_REFRESH_FORCE = "force";
const BUILD_REFRESH_NATURAL = "natural";
const DEFAULT_BUILD_REFRESH_MODE = BUILD_REFRESH_FORCE;
const CHECK_INTERVAL_MS = 60 * 60 * 1000;
const FOREGROUND_CHECK_THROTTLE_MS = 15 * 1000;
const INITIAL_CHECK_DELAY_MS = 0;
const LEGACY_FORCE_REFRESH_PARAM = "__ll_refresh";
const LAST_CHECK_STORAGE_KEY = "ll:build-refresh:last-check-at";
const REFRESH_ATTEMPT_STORAGE_KEY = "ll:build-refresh:attempted-build";
const CURRENT_BUILD_STORAGE_KEY = "ll:build-refresh:current-build";
const CHUNK_RECOVERY_STORAGE_KEY = "ll:chunk-recovery:last-attempt";
const FIRESTORE_CLIENT_RECOVERY_STORAGE_KEY =
  "ll:firestore-client-recovery:last-attempt";
const DEFAULT_CHUNK_RECOVERY_DELAY_MS = 750;
const DEFAULT_CHUNK_RECOVERY_COOLDOWN_MS = 5 * 60 * 1000;
const DEFAULT_FIRESTORE_CLIENT_RECOVERY_DELAY_MS = 750;
const DEFAULT_FIRESTORE_CLIENT_RECOVERY_COOLDOWN_MS = 5 * 60 * 1000;
const CHUNK_LOAD_FAILURE_RE =
  /(chunkloaderror|loading (?:css )?chunk|failed to fetch dynamically imported module|importing a module script failed)/iu;

const getDefaultWindow = () =>
  typeof window !== "undefined" ? window : null;

const getDefaultDocument = () =>
  typeof document !== "undefined" ? document : null;

const isLocalHost = (hostname = getDefaultWindow()?.location?.hostname) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "[::1]" ||
  String(hostname || "").endsWith(".local");

const shouldUseBuildRefresh = (windowRef = getDefaultWindow()) =>
  process.env.NODE_ENV === "production" &&
  !isLocalHost(windowRef?.location?.hostname);

const getWindowStorage = (windowRef, storageName) => {
  try {
    return windowRef?.[storageName] || null;
  } catch (error) {
    return null;
  }
};

const readStorage = (storage, key) => {
  try {
    if (typeof storage?.getItem !== "function") {
      return null;
    }

    return storage.getItem(key) || null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    if (typeof storage?.setItem !== "function") {
      return false;
    }

    storage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const removeStorage = (storage, key) => {
  try {
    storage?.removeItem(key);
  } catch (error) {
    // Ignore storage failures so refresh checks stay non-blocking.
  }
};

const readJsonStorage = (storage, key) => {
  const rawValue = readStorage(storage, key);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return null;
  }
};

export const shouldCheckForNewBuild = (
  lastCheckedAt,
  now = Date.now(),
  intervalMs = CHECK_INTERVAL_MS,
) => {
  if (!Number.isFinite(lastCheckedAt)) {
    return true;
  }

  return now - lastCheckedAt >= intervalMs;
};

export const stripForcedRefreshParam = (href) => {
  const url = new URL(href, getDefaultWindow()?.location?.origin);

  if (!url.searchParams.has(LEGACY_FORCE_REFRESH_PARAM)) {
    return null;
  }

  url.searchParams.delete(LEGACY_FORCE_REFRESH_PARAM);

  return `${url.pathname}${url.search}${url.hash}`;
};

export const shouldRefreshForNewBuild = ({
  currentMainAssetPath,
  latestMainAssetPath,
  latestBuildIdentifier,
  attemptedBuildIdentifier,
}) =>
  Boolean(
    currentMainAssetPath &&
      latestMainAssetPath &&
      latestBuildIdentifier &&
      currentMainAssetPath !== latestMainAssetPath &&
      attemptedBuildIdentifier !== latestBuildIdentifier,
  );

export const normalizeBuildRefreshMode = (value) =>
  String(value || "").trim().toLowerCase() === BUILD_REFRESH_NATURAL
    ? BUILD_REFRESH_NATURAL
    : DEFAULT_BUILD_REFRESH_MODE;

export const getBuildRefreshModeFromPolicy = (policy = {}) =>
  normalizeBuildRefreshMode(policy?.buildRefresh?.mode || policy?.mode);

const fetchBuildRefreshMode = async ({ fetchImpl, nowMs }) => {
  try {
    const response = await fetchImpl(`${BUILD_REFRESH_POLICY_PATH}?t=${nowMs}`, {
      cache: "no-store",
      credentials: "same-origin",
    });

    if (!response?.ok) {
      return DEFAULT_BUILD_REFRESH_MODE;
    }

    return getBuildRefreshModeFromPolicy(await response.json());
  } catch (error) {
    return DEFAULT_BUILD_REFRESH_MODE;
  }
};

const buildPageRecoveryKey = ({ windowRef, documentRef } = {}) => {
  const origin = windowRef?.location?.origin || "http://localhost";
  const currentMainAssetPath =
    getCurrentMainAssetPath(documentRef, origin) || "unknown-build";
  const pathname = windowRef?.location?.pathname || "/";

  return `${currentMainAssetPath}|${pathname}`;
};

export const shouldAttemptPageRecovery = ({
  lastAttempt = null,
  recoveryKey = "",
  nowMs = Date.now(),
  cooldownMs = DEFAULT_CHUNK_RECOVERY_COOLDOWN_MS,
} = {}) => {
  if (!recoveryKey) {
    return false;
  }

  if (!lastAttempt || lastAttempt.key !== recoveryKey) {
    return true;
  }

  const lastAttemptAt = Number(lastAttempt.at);
  if (!Number.isFinite(lastAttemptAt)) {
    return true;
  }

  return nowMs - lastAttemptAt >= cooldownMs;
};

const attemptPageReloadRecovery = ({
  enabled = null,
  windowRef = getDefaultWindow(),
  documentRef = getDefaultDocument(),
  now = Date.now,
  delayMs = DEFAULT_CHUNK_RECOVERY_DELAY_MS,
  cooldownMs = DEFAULT_CHUNK_RECOVERY_COOLDOWN_MS,
  storageKey,
  reason,
} = {}) => {
  const recoveryEnabled =
    typeof enabled === "boolean" ? enabled : shouldUseBuildRefresh(windowRef);

  if (!windowRef || !documentRef || !recoveryEnabled) {
    return { scheduled: false, reason: "disabled" };
  }

  const recoveryKey = buildPageRecoveryKey({ windowRef, documentRef });
  const nowMs = now();
  const sessionStorage = getWindowStorage(windowRef, "sessionStorage");
  const lastAttempt = readJsonStorage(sessionStorage, storageKey);

  if (
    !shouldAttemptPageRecovery({
      lastAttempt,
      recoveryKey,
      nowMs,
      cooldownMs,
    })
  ) {
    return { scheduled: false, reason: "already_attempted" };
  }

  const recorded = writeStorage(
    sessionStorage,
    storageKey,
    JSON.stringify({
      key: recoveryKey,
      at: nowMs,
    }),
  );

  if (!recorded) {
    return { scheduled: false, reason: "storage_unavailable" };
  }

  const reload = () => windowRef.location?.reload?.();

  if (delayMs > 0 && typeof windowRef.setTimeout === "function") {
    windowRef.setTimeout(reload, delayMs);
  } else {
    reload();
  }

  return { scheduled: true, reason };
};

export const shouldAttemptChunkRecovery = shouldAttemptPageRecovery;

export const attemptChunkLoadRecovery = ({
  enabled = null,
  windowRef = getDefaultWindow(),
  documentRef = getDefaultDocument(),
  now = Date.now,
  delayMs = DEFAULT_CHUNK_RECOVERY_DELAY_MS,
  cooldownMs = DEFAULT_CHUNK_RECOVERY_COOLDOWN_MS,
} = {}) => {
  return attemptPageReloadRecovery({
    enabled,
    windowRef,
    documentRef,
    now,
    delayMs,
    cooldownMs,
    storageKey: CHUNK_RECOVERY_STORAGE_KEY,
    reason: "chunk_load_failure",
  });
};

export const attemptFirestoreClientRecovery = ({
  enabled = null,
  windowRef = getDefaultWindow(),
  documentRef = getDefaultDocument(),
  now = Date.now,
  delayMs = DEFAULT_FIRESTORE_CLIENT_RECOVERY_DELAY_MS,
  cooldownMs = DEFAULT_FIRESTORE_CLIENT_RECOVERY_COOLDOWN_MS,
} = {}) =>
  attemptPageReloadRecovery({
    enabled,
    windowRef,
    documentRef,
    now,
    delayMs,
    cooldownMs,
    storageKey: FIRESTORE_CLIENT_RECOVERY_STORAGE_KEY,
    reason: "firestore_client_failure",
  });

export const isChunkLoadFailureMessage = ({
  message = "",
  name = "",
  stack = "",
} = {}) => CHUNK_LOAD_FAILURE_RE.test(`${name} ${message} ${stack}`);

export const attemptRefreshForNewBuild = async ({
  enabled = null,
  fetchImpl = getDefaultWindow()?.fetch?.bind(getDefaultWindow()),
  windowRef = getDefaultWindow(),
  documentRef = getDefaultDocument(),
  now = Date.now,
  refreshMode = null,
} = {}) => {
  const refreshEnabled =
    typeof enabled === "boolean" ? enabled : shouldUseBuildRefresh(windowRef);

  if (!windowRef || !documentRef || !fetchImpl || !refreshEnabled) {
    return { refreshed: false, reason: "disabled" };
  }

  try {
    const nowMs = now();
    const response = await fetchImpl(`${BUILD_MANIFEST_PATH}?t=${nowMs}`, {
      cache: "no-store",
      credentials: "same-origin",
    });

    if (!response.ok) {
      return { refreshed: false, reason: "manifest_unavailable" };
    }

    const manifest = await response.json();
    const origin = windowRef.location?.origin || "http://localhost";
    const latestBuildIdentifier = getManifestBuildIdentifier(manifest, origin);
    const currentMainAssetPath = getCurrentMainAssetPath(documentRef, origin);
    const latestMainAssetPath = getManifestMainAssetPath(manifest, origin);
    const localStorage = getWindowStorage(windowRef, "localStorage");
    const sessionStorage = getWindowStorage(windowRef, "sessionStorage");
    writeStorage(localStorage, LAST_CHECK_STORAGE_KEY, String(nowMs));
    const attemptedBuildIdentifier = readStorage(
      sessionStorage,
      REFRESH_ATTEMPT_STORAGE_KEY,
    );

    if (
      !currentMainAssetPath ||
      !latestMainAssetPath ||
      !latestBuildIdentifier
    ) {
      return { refreshed: false, reason: "missing_build_identifier" };
    }

    if (currentMainAssetPath === latestMainAssetPath) {
      writeStorage(
        sessionStorage,
        CURRENT_BUILD_STORAGE_KEY,
        latestBuildIdentifier,
      );
      removeStorage(sessionStorage, REFRESH_ATTEMPT_STORAGE_KEY);
      return { refreshed: false, reason: "current_build" };
    }

    if (
      !shouldRefreshForNewBuild({
        currentMainAssetPath,
        latestMainAssetPath,
        latestBuildIdentifier,
        attemptedBuildIdentifier,
      })
    ) {
      return { refreshed: false, reason: "already_attempted" };
    }

    const mode =
      refreshMode === null
        ? await fetchBuildRefreshMode({ fetchImpl, nowMs })
        : normalizeBuildRefreshMode(refreshMode);

    if (mode === BUILD_REFRESH_NATURAL) {
      return { refreshed: false, reason: "natural_refresh_policy" };
    }

    const recordedRefreshAttempt = writeStorage(
      sessionStorage,
      REFRESH_ATTEMPT_STORAGE_KEY,
      latestBuildIdentifier,
    );

    if (!recordedRefreshAttempt) {
      return { refreshed: false, reason: "storage_unavailable" };
    }

    windowRef.location.reload();
    return { refreshed: true, reason: "new_build" };
  } catch (error) {
    return { refreshed: false, reason: "check_failed" };
  }
};

export {
  BUILD_REFRESH_FORCE,
  BUILD_REFRESH_NATURAL,
  BUILD_REFRESH_POLICY_PATH,
  BUILD_MANIFEST_PATH,
  CHUNK_RECOVERY_STORAGE_KEY,
  CHECK_INTERVAL_MS,
  DEFAULT_BUILD_REFRESH_MODE,
  DEFAULT_CHUNK_RECOVERY_COOLDOWN_MS,
  DEFAULT_CHUNK_RECOVERY_DELAY_MS,
  DEFAULT_FIRESTORE_CLIENT_RECOVERY_COOLDOWN_MS,
  DEFAULT_FIRESTORE_CLIENT_RECOVERY_DELAY_MS,
  FIRESTORE_CLIENT_RECOVERY_STORAGE_KEY,
  FOREGROUND_CHECK_THROTTLE_MS,
  getCurrentMainAssetPath,
  getManifestBuildIdentifier,
  getManifestMainAssetPath,
  INITIAL_CHECK_DELAY_MS,
  LAST_CHECK_STORAGE_KEY,
  normalizeAssetPath,
};
